// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    showOrderDate = "showOrderDate",
    showOrderPo = "showOrderPo",
    showOrderDetailsStatus = "showOrderDetailsStatus",
    showOrderDetailsRequestedDate = "showOrderDetailsRequestedDate",
    showOrderDetailsPaymentTerms = "showOrderDetailsPaymentTerms",
    showOrderSalesperson = "showOrderSalesperson",
    showOrderDetailsVmiLocation = "showOrderDetailsVmiLocation",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOrderDate]: boolean;
        [fields.showOrderPo]: boolean;
        [fields.showOrderDetailsStatus]: boolean;
        [fields.showOrderDetailsRequestedDate]: boolean;
        [fields.showOrderDetailsPaymentTerms]: boolean;
        [fields.showOrderSalesperson]: boolean;
        [fields.showOrderDetailsVmiLocation]: boolean;
    };
}

type Props = OwnProps;

interface OrderDetailsInformationStyles {
    container?: InjectableCss;
}

const orderDetailsInformation: OrderDetailsInformationStyles = {
    container: {
        css: css`
            display: flex;
            margin-bottom: 4px;
            padding-bottom: 0;
            gap: 25px;
            flex-wrap: wrap;
            align-items: flex-start;
            ${TypographyStyle}:not(span) {
                font-size: 16px;
                line-height: 22px;
            }
            ${TypographyStyle} {
                font-size: 14px;
                line-height: 22px;
            }
            /* XNG-Changes: FLIP-169 */
            div {
                width: 150px;
                @media print {
                    margin-right: 8px;
                }
            }
        `,
    },
};

const styles = orderDetailsInformation;
const OrderDetailsInformation: FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.container}>
            {fields.showOrderDate &&
                createWidgetElement("OrderDetails/OrderDate", {
                    fields: {},
                })}
            {fields.showOrderPo &&
                createWidgetElement("OrderDetails/OrderPo", {
                    fields: {},
                })}
            {fields.showOrderDetailsStatus &&
                createWidgetElement("OrderDetails/OrderDetailsStatus", {
                    fields: {},
                })}
            {fields.showOrderDetailsRequestedDate &&
                createWidgetElement("OrderDetails/OrderDetailsRequestedDate", {
                    fields: {},
                })}
            {fields.showOrderDetailsPaymentTerms &&
                createWidgetElement("OrderDetails/OrderDetailsPaymentTerms", {
                    fields: {},
                })}
            {fields.showOrderSalesperson &&
                createWidgetElement("OrderDetails/OrderSalesperson", {
                    fields: {},
                })}
            {fields.showOrderDetailsVmiLocation &&
                createWidgetElement("OrderDetails/OrderDetailsVmiLocation", {
                    fields: {},
                })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsInformation,
    definition: {
        group: "Order Details",
        displayName: "Order Details Information",
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showOrderDate,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOrderDetailsRequestedDate,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showOrderDetailsStatus,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showOrderDetailsVmiLocation,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showOrderDetailsPaymentTerms,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showOrderPo,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showOrderSalesperson,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
        ],
    },
};

export default widgetModule;
